import { useState, useEffect } from 'react';
import { Palette } from 'lucide-react';

interface ColorPickerProps {
  value: string; // HEX 색상 (예: "#C41E3A")
  onChange: (color: string) => void;
  label?: string;
}

export default function ColorPicker({ value, onChange, label }: ColorPickerProps) {
  const [mode, setMode] = useState<'palette' | 'rgb' | 'hex'>('palette');
  const [rgb, setRgb] = useState({ r: 0, g: 0, b: 0 });
  const [hexInput, setHexInput] = useState('');

  // HEX를 RGB로 변환
  const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  };

  // RGB를 HEX로 변환
  const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (n: number) => {
      const hex = Math.max(0, Math.min(255, Math.round(n))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  };

  // value가 변경되면 RGB와 HEX 입력 업데이트
  useEffect(() => {
    const rgbValue = hexToRgb(value);
    setRgb(rgbValue);
    setHexInput(value.toUpperCase());
  }, [value]);

  // RGB 값 변경 처리
  const handleRgbChange = (channel: 'r' | 'g' | 'b', newValue: string) => {
    const numValue = parseInt(newValue) || 0;
    const clampedValue = Math.max(0, Math.min(255, numValue));
    const newRgb = { ...rgb, [channel]: clampedValue };
    setRgb(newRgb);
    onChange(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
  };

  // HEX 입력 변경 처리
  const handleHexChange = (newHex: string) => {
    setHexInput(newHex);
    // HEX 형식 검증 (# 포함 여부 관계없이)
    const hexPattern = /^#?[0-9A-Fa-f]{6}$/;
    const normalizedHex = newHex.startsWith('#') ? newHex : `#${newHex}`;

    if (hexPattern.test(normalizedHex)) {
      onChange(normalizedHex.toUpperCase());
    }
  };

  // 프리셋 색상 (4행 x 10열 = 40개)
  const presetColors = [
    // 1행: 브랜드 & 빨강 계열
    '#C41E3A', // 빨강 (기본)
    '#1E3A5F', // Navy Blue (브랜드)
    '#F5821F', // Orange (브랜드)
    '#DC2626', // 진한 빨강
    '#EF4444', // 빨강
    '#F87171', // 연한 빨강
    '#FB923C', // 주황
    '#FBBF24', // 노랑
    '#FDE047', // 연한 노랑
    '#FEF3C7', // 아주 연한 노랑

    // 2행: 초록 계열
    '#15803D', // 진한 초록
    '#22C55E', // 초록
    '#4ADE80', // 연한 초록
    '#86EFAC', // 아주 연한 초록
    '#14B8A6', // 청록
    '#2DD4BF', // 연한 청록
    '#5EEAD4', // 아주 연한 청록
    '#06B6D4', // 하늘
    '#22D3EE', // 연한 하늘
    '#7DD3FC', // 아주 연한 하늘

    // 3행: 파랑 & 보라 계열
    '#1D4ED8', // 진한 파랑
    '#3B82F6', // 파랑
    '#60A5FA', // 연한 파랑
    '#93C5FD', // 아주 연한 파랑
    '#7C3AED', // 보라
    '#8B5CF6', // 연한 보라
    '#A78BFA', // 아주 연한 보라
    '#C084FC', // 핑크 보라
    '#E879F9', // 연한 핑크 보라
    '#F0ABFC', // 아주 연한 핑크

    // 4행: 핑크 & 무채색
    '#DB2777', // 진한 핑크
    '#EC4899', // 핑크
    '#F472B6', // 연한 핑크
    '#F9A8D4', // 아주 연한 핑크
    '#000000', // 검정
    '#374151', // 진한 회색
    '#6B7280', // 회색
    '#9CA3AF', // 연한 회색
    '#D1D5DB', // 아주 연한 회색
    '#FFFFFF', // 흰색
  ];

  return (
    <div className="space-y-3">
      {/* 라벨 */}
      {label && <label className="block text-sm font-semibold text-gray-900">{label}</label>}

      {/* 현재 색상 미리보기 */}
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-16 rounded-lg border-2 border-gray-300 shadow-sm"
          style={{ backgroundColor: value }}
        />
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-700">현재 색상</div>
          <div className="text-xs text-gray-500 font-mono">{value}</div>
          <div className="text-xs text-gray-500">
            RGB({rgb.r}, {rgb.g}, {rgb.b})
          </div>
        </div>
      </div>

      {/* 모드 선택 탭 */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setMode('palette')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'palette'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Palette className="w-4 h-4 inline mr-1" />
          팔레트
        </button>
        <button
          type="button"
          onClick={() => setMode('rgb')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'rgb'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          RGB
        </button>
        <button
          type="button"
          onClick={() => setMode('hex')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            mode === 'hex'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          HEX
        </button>
      </div>

      {/* 팔레트 모드 */}
      {mode === 'palette' && (
        <div className="grid grid-cols-10 gap-x-0 gap-y-0.5">
          {presetColors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => onChange(color)}
              className={`w-6 h-6 transition-all hover:scale-110 ${
                value.toUpperCase() === color.toUpperCase()
                  ? 'ring-2 ring-primary z-10'
                  : ''
              }`}
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      )}

      {/* RGB 모드 */}
      {mode === 'rgb' && (
        <div className="space-y-3">
          {/* R */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Red (0-255)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="255"
                value={rgb.r}
                onChange={(e) => handleRgbChange('r', e.target.value)}
                className="flex-1 h-2 bg-red-200 rounded-lg appearance-none cursor-pointer slider-thumb"
              />
              <input
                type="number"
                min="0"
                max="255"
                value={rgb.r}
                onChange={(e) => handleRgbChange('r', e.target.value)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* G */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Green (0-255)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="255"
                value={rgb.g}
                onChange={(e) => handleRgbChange('g', e.target.value)}
                className="flex-1 h-2 bg-green-200 rounded-lg appearance-none cursor-pointer slider-thumb"
              />
              <input
                type="number"
                min="0"
                max="255"
                value={rgb.g}
                onChange={(e) => handleRgbChange('g', e.target.value)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* B */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Blue (0-255)</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="0"
                max="255"
                value={rgb.b}
                onChange={(e) => handleRgbChange('b', e.target.value)}
                className="flex-1 h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer slider-thumb"
              />
              <input
                type="number"
                min="0"
                max="255"
                value={rgb.b}
                onChange={(e) => handleRgbChange('b', e.target.value)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
        </div>
      )}

      {/* HEX 모드 */}
      {mode === 'hex' && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            HEX 색상 코드 (#RRGGBB)
          </label>
          <input
            type="text"
            value={hexInput}
            onChange={(e) => handleHexChange(e.target.value)}
            placeholder="#C41E3A"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            maxLength={7}
          />
          <p className="text-xs text-gray-500 mt-1">
            # 기호는 선택사항입니다. (예: C41E3A 또는 #C41E3A)
          </p>
        </div>
      )}

      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          border: 2px solid #1E3A5F;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .slider-thumb::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          border: 2px solid #1E3A5F;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
}
